// exposing an object with the features, so that we can rely on autocomplete,
// and to avoid using strings that could be misspelled or changed in the future.
export const platformFeatures = {
  HOST_MONITORING: "host_monitoring",
  CLUSTER_MONITORING: "kubernetes",
  PERFORMANCE_ISSUE_LIST: "performance_issue_list",
  PERFORMANCE_TRACES: "performance_traces",
  PERFORMANCE_SLOW_EVENTS: "performance_slow_events",
  PERFORMANCE_SLOW_API_REQUESTS: "performance_slow_api_requests",
  PERFORMANCE_SLOW_QUERIES: "performance_slow_queries",
  TRIGGERS_SAMPLE_TRIGGERS: "triggers_sample_triggers",
  TRIGGERS_TRACING_TRIGGERS: "triggers_tracing_triggers",
}

const hideForPlatforms = {
  logplex: [
    platformFeatures.PERFORMANCE_TRACES,
    platformFeatures.CLUSTER_MONITORING,
    platformFeatures.TRIGGERS_TRACING_TRIGGERS,
  ],
  logplexv1: [
    platformFeatures.PERFORMANCE_TRACES,
    platformFeatures.CLUSTER_MONITORING,
    platformFeatures.TRIGGERS_TRACING_TRIGGERS,
  ],
  logplexv2: [
    platformFeatures.PERFORMANCE_TRACES,
    platformFeatures.CLUSTER_MONITORING,
    platformFeatures.TRIGGERS_TRACING_TRIGGERS,
  ],
  heroku: [
    platformFeatures.PERFORMANCE_TRACES,
    platformFeatures.CLUSTER_MONITORING,
    platformFeatures.TRIGGERS_TRACING_TRIGGERS,
  ],
  dokku: [
    platformFeatures.PERFORMANCE_TRACES,
    platformFeatures.CLUSTER_MONITORING,
    platformFeatures.TRIGGERS_TRACING_TRIGGERS,
  ],
  vercel: [
    platformFeatures.PERFORMANCE_TRACES,
    platformFeatures.CLUSTER_MONITORING,
    platformFeatures.TRIGGERS_TRACING_TRIGGERS,
  ],
  opentelemetry: [
    platformFeatures.PERFORMANCE_ISSUE_LIST,
    platformFeatures.PERFORMANCE_SLOW_EVENTS,
    platformFeatures.CLUSTER_MONITORING,
    platformFeatures.TRIGGERS_SAMPLE_TRIGGERS,
  ],
  samples: [
    platformFeatures.PERFORMANCE_TRACES,
    platformFeatures.CLUSTER_MONITORING,
    platformFeatures.TRIGGERS_TRACING_TRIGGERS,
  ],
  frontend_errors: [
    platformFeatures.PERFORMANCE_TRACES,
    platformFeatures.CLUSTER_MONITORING,
    platformFeatures.TRIGGERS_TRACING_TRIGGERS,
  ],
  kubernetes: [platformFeatures.TRIGGERS_TRACING_TRIGGERS],
}

export const PlatformContext = React.createContext([])

export const usePlatforms = () => {
  const platforms = React.useContext(PlatformContext)
  return platforms
}

const usePlatformFeature = (featureList = hideForPlatforms) => {
  const platforms = React.useContext(PlatformContext)

  const hasPlatform = (platform) => platforms.includes(platform)

  const isFeatureVisible = (feature) => {
    const filteredPlatforms = platforms.filter((platform) =>
      Object.keys(hideForPlatforms).includes(platform),
    )

    if (filteredPlatforms.length === 0) {
      // If there are no platforms, we show everything except the open-telemetry
      // We explicitly handle the open-telemetry case because of this edge case
      // https://appsignal.slack.com/archives/C01HXL10GC8/p1731665421095619
      return (
        feature !== platformFeatures.PERFORMANCE_TRACES &&
        feature !== platformFeatures.TRIGGERS_TRACING_TRIGGERS
      )
    }
    const featuresToBeHidden = []

    // We also include languages into platforms,
    // so we need to filter out any plarforms not in hideForPlatforms lookup table.
    filteredPlatforms.forEach((platform) => {
      if (Object.keys(featureList).includes(platform)) {
        featuresToBeHidden.push(...featureList[platform])
      }
    })

    const filteredFeatureList = featuresToBeHidden.filter(
      (potentialFeature) => potentialFeature === feature,
    )

    return filteredFeatureList.length !== filteredPlatforms.length
  }

  return { isFeatureVisible, hasPlatform }
}

export default usePlatformFeature
